import React, { useState, useEffect } from 'react';
import { useUser } from '../../../contexts/userContext';
import { useCard } from '../../../contexts/cardContext';
import api from '../../../services/api';
import './style.css';

function EmailModal() {
  const { user, empresa, listAllUsers } = useUser();
  const { 
    currentCardData, 
    setOpenCloseEmailModal,
    addHistoricoCardContext 
  } = useCard();

  const [recipients, setRecipients] = useState([]);
  const [currentRecipient, setCurrentRecipient] = useState('');
  const [emailData, setEmailData] = useState({
    subject: '',
    text: ''
  });
  const [loading, setLoading] = useState(false);

  // Emojis mais usados
  const commonEmojis = ['😊', '👍', '❤️', '😁', '👏', '🎉', '✅', '🙏', '💪', '🔥', '😎', '🎯'];

  // Funções auxiliares para obter emails
  const getEmailByEntityId = (entityId) => {
    if (!listAllUsers) return null;
    const entidade = listAllUsers.find(afilhado => afilhado.id === entityId);
    if (entidade && entidade.email) {
      return entidade.email;
    }
    if (entityId === user.id && user.email) {
      return user.email;
    }
    return null;
  };

  const getUsernameById = (entityId) => {
    if (!listAllUsers) return 'Erro: Dados necessários não estão disponíveis.';
    const entidade = listAllUsers.find(afilhado => afilhado.id === entityId);
    if (entidade && entidade.username) {
      return entidade.username;
    }
    if (entityId === user.id) {
      return user.username;
    }
    return 'id não encontrado';
  };

  // Inicializar com dados do card quando aberto
  useEffect(() => {
    if (currentCardData) {
      // Pré-preencher assunto com número do documento
      const assunto = currentCardData.document_number 
        ? `Proposta ${currentCardData.document_number}`
        : currentCardData.name 
          ? `Sobre ${currentCardData.name}`
          : '';
      
      // Pré-preencher mensagem padrão
      const mensagemPadrao = `Prezado(a),

Gostaria de entrar em contato referente à ${currentCardData.document_number ? `proposta ${currentCardData.document_number}` : 'atendimento'}.

${currentCardData.name ? `Cliente: ${currentCardData.name}` : ''}
${currentCardData.city && currentCardData.state ? `Localização: ${currentCardData.city}/${currentCardData.state}` : ''}
${currentCardData.sale_value ? `Valor: ${parseFloat(currentCardData.sale_value).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}` : ''}

Atenciosamente,
${user.username || user.email || ''}`;
      
      setEmailData(prev => ({
        ...prev,
        subject: assunto,
        text: mensagemPadrao
      }));
    }
  }, [currentCardData, user]);

  // Adicionar destinatário
  const addRecipient = (email) => {
    if (!email || !email.trim()) return;
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      alert('Por favor, informe um email válido.');
      return;
    }

    if (recipients.includes(email.trim())) {
      alert('Este email já está na lista de destinatários.');
      return;
    }

    setRecipients(prev => [...prev, email.trim()]);
    setCurrentRecipient('');
  };

  // Remover destinatário
  const removeRecipient = (email) => {
    setRecipients(prev => prev.filter(e => e !== email));
  };

  // Adicionar email do cliente (do card)
  const addClientEmail = () => {
    if (currentCardData && currentCardData.email) {
      addRecipient(currentCardData.email);
    } else {
      alert('O card não possui email do cliente cadastrado.');
    }
  };

  // Adicionar email da entidade responsável
  const addEntityEmail = () => {
    if (currentCardData && currentCardData.entity_id) {
      const entityEmail = getEmailByEntityId(currentCardData.entity_id);
      if (entityEmail) {
        addRecipient(entityEmail);
      } else {
        alert('Não foi possível encontrar o email da entidade responsável.');
      }
    } else {
      alert('Não há entidade responsável associada a este card.');
    }
  };

  // Adicionar email do usuário logado
  const addUserEmail = () => {
    if (user && user.email) {
      addRecipient(user.email);
    } else {
      alert('Não foi possível encontrar o email do usuário logado.');
    }
  };

  const handleInputChange = (field, value) => {
    setEmailData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Função para inserir emoji na posição do cursor
  const insertEmoji = (emoji) => {
    const textarea = document.getElementById('email-send-modal-text');
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = emailData.text;
    
    const newText = text.substring(0, start) + emoji + text.substring(end);
    
    setEmailData(prev => ({
      ...prev,
      text: newText
    }));

    // Restaurar posição do cursor após o emoji
    setTimeout(() => {
      textarea.focus();
      const newPosition = start + emoji.length;
      textarea.setSelectionRange(newPosition, newPosition);
    }, 0);
  };

  const handleAddRecipientKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addRecipient(currentRecipient);
    }
  };

  const handleSendEmail = async (e) => {
    e.preventDefault();

    if (recipients.length === 0) {
      alert('Por favor, adicione pelo menos um destinatário.');
      return;
    }

    if (!emailData.subject.trim()) {
      alert('Por favor, informe o assunto do email.');
      return;
    }

    if (!emailData.text.trim()) {
      alert('Por favor, informe a mensagem do email.');
      return;
    }

    try {
      setLoading(true);
      
      // Enviar email para cada destinatário
      const emailPromises = recipients.map(to => 
        api.post(`/users/send-email/${user.empresa_id}`, {
          to,
          subject: emailData.subject,
          text: emailData.text
        })
      );

      await Promise.all(emailPromises);
      
      alert(`Email enviado com sucesso para ${recipients.length} destinatário(s)!`);
      
      // Adicionar ao histórico do card se houver um card associado
      if (currentCardData && currentCardData.card_id) {
        const recipientsList = recipients.join(', ');
        addHistoricoCardContext(
          `Email enviado para ${recipientsList}: ${emailData.subject}`,
          currentCardData.card_id,
          user.id
        );
      }

      // Limpar formulário
      setRecipients([]);
      setCurrentRecipient('');
      setEmailData({
        subject: '',
        text: ''
      });

      // Fechar modal
      setOpenCloseEmailModal(false);
    } catch (error) {
      console.error('Erro ao enviar email:', error);
      alert('Erro ao enviar o email. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setRecipients([]);
    setCurrentRecipient('');
    setEmailData({
      subject: '',
      text: ''
    });
    setOpenCloseEmailModal(false);
  };

  return (
    <div className='email-send-modal-overlay' onClick={handleClose}>
      <div className='email-send-modal-container' onClick={(e) => e.stopPropagation()}>
        <div className='email-send-modal-header'>
          <h3>Enviar Email</h3>
          <button className='email-send-modal-close-btn' onClick={handleClose}>X</button>
        </div>

        <form className='email-send-modal-form' onSubmit={handleSendEmail}>
          <div className='email-send-modal-field'>
            <label htmlFor='email-send-modal-to'>Destinatários:</label>
            <div className='email-send-modal-recipients-container'>
              <div className='email-send-modal-recipients-buttons'>
                {currentCardData && (
                  <>
                    {currentCardData.email && (
                      <button 
                        type='button'
                        className='email-send-modal-btn-add-email'
                        onClick={addClientEmail}
                      >
                        + Email do Cliente
                      </button>
                    )}
                    {currentCardData.entity_id && (
                      <button 
                        type='button'
                        className='email-send-modal-btn-add-email'
                        onClick={addEntityEmail}
                      >
                        + Email da Entidade
                      </button>
                    )}
                  </>
                )}
                {user && user.email && (
                  <button 
                    type='button'
                    className='email-send-modal-btn-add-email'
                    onClick={addUserEmail}
                  >
                    + Meu Email
                  </button>
                )}
              </div>
              <div className='email-send-modal-recipients-input-container'>
                <input
                  type='email'
                  id='email-send-modal-to'
                  value={currentRecipient}
                  onChange={(e) => setCurrentRecipient(e.target.value)}
                  onKeyPress={handleAddRecipientKeyPress}
                  placeholder='Digite o email e pressione Enter'
                />
                <button
                  type='button'
                  className='email-send-modal-btn-add'
                  onClick={() => addRecipient(currentRecipient)}
                >
                  Adicionar
                </button>
              </div>
              {recipients.length > 0 && (
                <div className='email-send-modal-recipients-list'>
                  {recipients.map((email, index) => (
                    <div key={index} className='email-send-modal-recipient-tag'>
                      <span>{email}</span>
                      <button
                        type='button'
                        className='email-send-modal-remove-recipient'
                        onClick={() => removeRecipient(email)}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className='email-send-modal-field'>
            <label htmlFor='email-send-modal-subject'>Assunto:</label>
            <input
              type='text'
              id='email-send-modal-subject'
              value={emailData.subject}
              onChange={(e) => handleInputChange('subject', e.target.value)}
              placeholder='Assunto do email'
              required
            />
          </div>

          <div className='email-send-modal-field'>
            <div className='email-send-modal-label-with-emojis'>
              <label htmlFor='email-send-modal-text'>Mensagem:</label>
              <div className='email-send-modal-emojis-container'>
                {commonEmojis.map((emoji, index) => (
                  <button
                    key={index}
                    type='button'
                    className='email-send-modal-emoji-btn'
                    onClick={() => insertEmoji(emoji)}
                    title={`Inserir ${emoji}`}
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            </div>
            <textarea
              id='email-send-modal-text'
              value={emailData.text}
              onChange={(e) => handleInputChange('text', e.target.value)}
              placeholder='Digite sua mensagem aqui...'
              rows={17}
              required
            />
          </div>

          <div className='email-send-modal-actions'>
            <button 
              type='button' 
              className='email-send-modal-btn-cancel' 
              onClick={handleClose}
              disabled={loading}
            >
              Cancelar
            </button>
            <button 
              type='submit' 
              className='email-send-modal-btn-send' 
              disabled={loading}
            >
              {loading ? 'Enviando...' : 'Enviar Email'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default EmailModal;

